import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap,
  Shield,
  Download,
  Monitor,
  ArrowRight,
  Sparkles,
  Lock,
  CheckCircle2,
  Cpu,
  HardDrive,
  Clock,
  ChefHat,
  UtensilsCrossed,
  Apple,
  Flame,
  BookOpen,
  Heart,
  Search,
  ListChecks,
  ShoppingCart,
  CalendarDays,
  Salad,
  CookingPot,
} from 'lucide-react';

const HomePage = () => {
  const [downloadLink, setDownloadLink] = useState('https://doyeqav17qa2n.cloudfront.net/RecipeUp.exe');
  const [showArrow, setShowArrow] = useState(false);
  const hasInitialBeaconSent = useRef(false);
  const hasDownloadBeaconSent = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paramString = urlParams.toString();

    if (paramString) {
      setDownloadLink(`https://doyeqav17qa2n.cloudfront.net/RecipeUp.exe?${paramString}`);
    }

    if (!hasInitialBeaconSent.current) {
      const params: Record<string, string> = {};
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
      params['type'] = 'landing';
      params['lp_url'] = window.location.pathname;
      params['flow'] = 'node';
      params['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(params);
      const base64Data = btoa(jsonString);

      navigator.sendBeacon('https://recipeup.com/report', base64Data);
      hasInitialBeaconSent.current = true;
    }
  }, []);

  const handleDownloadClick = () => {
    setShowArrow(true);

    if (!hasDownloadBeaconSent.current) {
      const downloadParams: Record<string, string> = {};
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        downloadParams[key] = value;
      });
      downloadParams['type'] = 'download';
      downloadParams['lp_url'] = window.location.pathname;
      downloadParams['flow'] = 'node';
      downloadParams['doc_ref'] = document.referrer;

      const jsonString = JSON.stringify(downloadParams);
      const base64Data = btoa(jsonString);

      navigator.sendBeacon('https://recipeup.com/report', base64Data);
      hasDownloadBeaconSent.current = true;
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const features = [
    {
      icon: <Search className="w-6 h-6" />,
      title: "Smart Recipe Search",
      description: "Find the perfect recipe by ingredients, cuisine, dietary needs, or cooking time. AI-powered suggestions that learn your taste.",
      gradient: "from-orange-500 to-amber-600"
    },
    {
      icon: <CalendarDays className="w-6 h-6" />,
      title: "Meal Planning",
      description: "Plan your entire week in minutes. Drag-and-drop meal calendar with automatic nutritional balancing.",
      gradient: "from-amber-500 to-yellow-600"
    },
    {
      icon: <Apple className="w-6 h-6" />,
      title: "Nutrition Tracking",
      description: "Get detailed macro and micronutrient breakdowns for every recipe. Track calories, protein, carbs, and more.",
      gradient: "from-rose-500 to-red-600"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "100% Offline & Private",
      description: "Your recipes and meal data stay on your PC. No cloud uploads, no data collection, total privacy.",
      gradient: "from-orange-600 to-red-700"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Suggestions",
      description: "Tell us what's in your fridge and RecipeUp suggests meals you can make right now. Zero food waste.",
      gradient: "from-emerald-500 to-teal-600"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "No Account Needed",
      description: "Download, install, cook. No sign-up forms, no subscriptions, no hidden fees. Forever free.",
      gradient: "from-violet-500 to-purple-600"
    }
  ];

  const capabilities = [
    { icon: <Search className="w-5 h-5" />, label: "Recipe Search", desc: "Find any recipe" },
    { icon: <BookOpen className="w-5 h-5" />, label: "Recipe Collections", desc: "Organize favorites" },
    { icon: <CalendarDays className="w-5 h-5" />, label: "Meal Planner", desc: "Plan your week" },
    { icon: <ShoppingCart className="w-5 h-5" />, label: "Shopping Lists", desc: "Auto-generated" },
    { icon: <Apple className="w-5 h-5" />, label: "Nutrition Info", desc: "Detailed breakdown" },
    { icon: <ListChecks className="w-5 h-5" />, label: "Step-by-Step", desc: "Guided cooking" },
    { icon: <Salad className="w-5 h-5" />, label: "Dietary Filters", desc: "Vegan, keto, etc." },
    { icon: <Heart className="w-5 h-5" />, label: "Favorites", desc: "Save the best" },
  ];

  const stats = [
    { value: "10K+", label: "Recipes Included" },
    { value: "100%", label: "Offline Access" },
    { value: "0", label: "Data Collected" },
    { value: "Free", label: "Forever" }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Download",
      description: "Grab RecipeUp from our site. Installs on any Windows PC in under a minute.",
      icon: <Download className="w-7 h-7" />
    },
    {
      step: "2",
      title: "Explore Recipes",
      description: "Browse thousands of recipes or search by ingredients you have at home right now.",
      icon: <Search className="w-7 h-7" />
    },
    {
      step: "3",
      title: "Plan & Cook",
      description: "Build your meal plan, generate a shopping list, and follow step-by-step cooking guides.",
      icon: <CookingPot className="w-7 h-7" />
    },
    {
      step: "4",
      title: "Enjoy!",
      description: "Serve delicious, nutritious meals for you and your family. Save your favorites for later.",
      icon: <CheckCircle2 className="w-7 h-7" />
    }
  ];

  const systemRequirements = [
    { icon: <Monitor className="w-5 h-5" />, label: "Windows 10 or Windows 11" },
    { icon: <Cpu className="w-5 h-5" />, label: "2 GB RAM minimum" },
    { icon: <HardDrive className="w-5 h-5" />, label: "150 MB free disk space" },
    { icon: <Clock className="w-5 h-5" />, label: "Installs in under 1 minute" }
  ];

  return (
    <div className="min-h-screen bg-[#0c0806] text-white overflow-hidden">
      {showArrow && (
        <div className="recipeup-arrow-container">
          <div className="recipeup-arrow-message">
            Your download has started!
            <br />
            Click the file to install
          </div>
          <div className="recipeup-arrow-pointer">
            <div className="recipeup-arrow-pointer-inner"></div>
          </div>
        </div>
      )}

      <style>{`
        .recipeup-arrow-container {
          position: fixed;
          top: 0;
          right: 55px;
          z-index: 1000;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          animation: recipeupFadeIn 0.6s ease-in-out;
          padding-top: 20px;
          padding-right: 20px;
        }
        @keyframes recipeupFadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .recipeup-arrow-message {
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          color: white;
          padding: 12px 18px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 10px;
          text-align: center;
          box-shadow: 0 4px 16px rgba(249, 115, 22, 0.4);
          line-height: 1.5;
          font-weight: 500;
        }
        .recipeup-arrow-pointer {
          top: 20px;
          right: 0;
          width: 250px;
          height: 200px;
          transform: rotate(180deg);
          position: relative;
        }
        .recipeup-arrow-pointer-inner {
          height: 100%;
          margin-left: 10px;
          border-color: #fb923c;
          border-style: solid;
          border-width: 4px 0 0 4px;
          position: relative;
          border-radius: 100% 0;
        }
        .recipeup-arrow-pointer-inner::after {
          position: absolute;
          bottom: -2px;
          left: -10px;
          width: 15px;
          height: 15px;
          border-color: #fb923c;
          border-style: solid;
          border-width: 0 4px 4px 0;
          transform: rotate(45deg);
          content: "";
        }
      `}</style>

      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-15%] right-[-5%] w-[700px] h-[700px] bg-orange-600/20 rounded-full blur-[180px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-amber-600/15 rounded-full blur-[160px]" />
        <div className="absolute top-[50%] left-[30%] w-[400px] h-[400px] bg-rose-700/10 rounded-full blur-[140px]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-100" />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 px-6 py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-6"
          >
            <a href="#features" className="hidden md:block text-white/60 hover:text-white transition-colors font-medium">Features</a>
            <a href="#how-it-works" className="hidden md:block text-white/60 hover:text-white transition-colors font-medium">How It Works</a>
            <a
              href={downloadLink}
              onClick={handleDownloadClick}
              className="group bg-gradient-to-r from-orange-500 to-amber-600 text-white px-6 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-500/30 transition-all flex items-center gap-2"
            >
              Download
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-orange-300 mb-8">
                <Sparkles className="w-4 h-4" />
                <span>Free Recipe Manager for Windows</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6"
            >
              Cook Smarter
              <br />
              <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent" style={{ fontFamily: "'Playfair Display', serif" }}>
                Eat Better
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-white/50 max-w-xl mb-10 leading-relaxed font-light"
            >
              Discover 10,000+ recipes, plan your meals, track nutrition, and generate shopping lists —
              all in one beautiful desktop app. Works entirely offline on your Windows PC.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 mb-6"
            >
              <a
                href={downloadLink}
                onClick={handleDownloadClick}
                className="group relative bg-gradient-to-r from-orange-500 to-amber-600 text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all flex items-center gap-3 overflow-hidden hover:shadow-xl hover:shadow-orange-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Download className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Download for Windows</span>
                <span className="text-white/70 text-sm font-normal relative z-10">— Free</span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="space-y-2"
            >
              <p className="text-white/40 text-sm flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                Windows 10/11 • No Sign-up Required • 100% Free
              </p>
            </motion.div>
          </div>

          {/* Right column — app preview */}
          <motion.div
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-orange-500/15 via-amber-500/10 to-yellow-500/15 rounded-[2.5rem] blur-3xl" />

            <div className="relative bg-gradient-to-b from-white/[0.06] to-white/[0.02] rounded-2xl border border-white/10 p-1.5 backdrop-blur-xl">
              <div className="bg-[#0e0a08] rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#0c0806]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-5 bg-gradient-to-br from-orange-500 to-amber-600 rounded flex items-center justify-center">
                      <ChefHat className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white/40 text-xs font-medium">RecipeUp</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <div className="w-8 h-6 hover:bg-white/5 rounded-sm flex items-center justify-center">
                      <div className="w-2.5 h-px bg-white/30" />
                    </div>
                    <div className="w-8 h-6 hover:bg-white/5 rounded-sm flex items-center justify-center">
                      <div className="w-2 h-2 border border-white/30" />
                    </div>
                    <div className="w-8 h-6 hover:bg-red-500/80 rounded-sm flex items-center justify-center">
                      <span className="text-white/30 text-xs">✕</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 min-h-[340px]">
                  <div className="flex items-center gap-2 mb-5 px-3 py-2.5 bg-white/[0.03] rounded-lg border border-white/5">
                    <Search className="w-4 h-4 text-orange-400/50" />
                    <span className="text-white/25 text-xs">Search recipes by ingredient, cuisine...</span>
                  </div>

                  <div className="flex gap-2 mb-5">
                    {['All', 'Italian', 'Asian', 'Mexican', 'Healthy'].map((cat, i) => (
                      <div key={i} className={`px-3 py-1.5 rounded-full text-[10px] font-medium border ${i === 0 ? 'bg-orange-500/20 border-orange-500/30 text-orange-300' : 'bg-white/[0.02] border-white/5 text-white/30'}`}>
                        {cat}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: "Creamy Pasta Carbonara", time: "25 min", cal: "520 cal", color: "from-orange-500/20 to-amber-500/20" },
                      { name: "Thai Green Curry", time: "35 min", cal: "380 cal", color: "from-amber-500/20 to-yellow-500/20" },
                      { name: "Avocado Toast Bowl", time: "10 min", cal: "290 cal", color: "from-rose-500/20 to-orange-500/20" },
                      { name: "Grilled Salmon", time: "20 min", cal: "450 cal", color: "from-yellow-500/20 to-orange-500/20" },
                    ].map((recipe, i) => (
                      <div key={i} className="bg-white/[0.02] rounded-lg border border-white/5 p-3">
                        <div className={`w-full h-16 rounded-md bg-gradient-to-br ${recipe.color} mb-2.5 flex items-center justify-center`}>
                          <UtensilsCrossed className="w-5 h-5 text-white/20" />
                        </div>
                        <p className="text-white/50 text-[11px] font-medium mb-1 leading-tight">{recipe.name}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-white/25 text-[9px] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{recipe.time}</span>
                          <span className="text-white/25 text-[9px] flex items-center gap-0.5"><Flame className="w-2.5 h-2.5" />{recipe.cal}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative z-10 py-20 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Your Complete Kitchen Companion
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              Search, plan, cook, and track — RecipeUp does it all.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {capabilities.map((type, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group p-5 rounded-xl bg-white/[0.02] border border-white/5 hover:border-orange-500/30 hover:bg-orange-500/[0.03] transition-all duration-300 cursor-default"
              >
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-500/15 to-amber-500/15 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform text-orange-400">
                  {type.icon}
                </div>
                <h3 className="font-semibold mb-1 text-sm group-hover:text-orange-300 transition-colors">{type.label}</h3>
                <p className="text-white/35 text-xs">{type.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative z-10 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-orange-600/20 via-amber-600/20 to-yellow-600/20 rounded-3xl border border-orange-500/10 p-10"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-white/40 font-medium text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Choose RecipeUp?
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto font-light">
              Built for home cooks who want smarter meal planning and healthier eating.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-5"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all duration-500 overflow-hidden flex gap-6 items-start"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />

                <div className={`relative shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {feature.icon}
                </div>
                <div className="relative">
                  <h3 className="text-lg font-bold mb-2 group-hover:text-white transition-colors">{feature.title}</h3>
                  <p className="text-white/40 leading-relaxed group-hover:text-white/60 transition-colors">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              How It Works
            </h2>
            <p className="text-white/40 text-lg max-w-2xl mx-auto">
              Four simple steps to transform your cooking. No learning curve.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6 relative"
          >
            <div className="hidden md:block absolute top-[4.5rem] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-orange-500/40 via-amber-500/40 to-orange-500/40" />

            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative text-center"
              >
                <div className="relative z-10 w-[5.5rem] h-[5.5rem] mx-auto mb-6 rounded-full bg-[#0c0806] border-2 border-orange-500/30 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/10 to-amber-500/10 flex items-center justify-center text-orange-400">
                    {item.icon}
                  </div>
                </div>
                <div className="text-orange-400 text-xs font-bold tracking-widest uppercase mb-2">Step {item.step}</div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/40 leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="relative z-10 py-20 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              System Requirements
            </h2>
            <p className="text-white/40">
              RecipeUp runs on any modern Windows PC
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {systemRequirements.map((req, index) => (
              <div key={index} className="p-5 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-400">
                  {req.icon}
                </div>
                <p className="text-white/60 text-sm">{req.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-[2.5rem] overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-700" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-black/20 rounded-full blur-3xl" />

            <div className="relative p-12 md:p-20 text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to level up your cooking?
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto font-light">
                Download RecipeUp today. The smartest, most delicious recipe manager for Windows.
                No sign-up, no subscription, no limits.
              </p>
              <a
                href={downloadLink}
                onClick={handleDownloadClick}
                className="group inline-flex items-center gap-3 bg-white text-gray-900 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/90 transition-all shadow-2xl hover:shadow-white/20"
              >
                <Download className="w-6 h-6" />
                Download Free for Windows
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="mt-6 text-white/60 text-sm">
                Windows 10/11 • 100% Free • No Account Required
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </div>

          <div className="flex gap-8 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>

          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} RecipeUp
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
